import React, { useRef } from 'react';
import { BrowserRouter, NavLink, Routes, Route } from 'react-router-dom'
import Cookies from 'universal-cookie';
import { BarChart3, Bell, ChevronLeft, ChevronRight, LayoutDashboard, LogOut, Mail, Map, Sparkles } from 'lucide-react';

import Topbar from './Topbar'
import Checkbox from './Checkbox'
import Chart1 from './Chart1'
import DisplayData from './DisplayData'

import logo from './images/logo.png'
import AlertSettings from './AlertSettings';
import AlertList from './AlertList'
import AlertEmail from './AlertEmail';
import { FloorPlan, FloorPlan2 } from './FloorPlan';
import Overview from './Overview';
import Analysis from './Analysis';
import config from './config';


function App() {
    const contentRef = useRef(null);
    const now = new Date()
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset())
    const timeFrom = new Date(now)
    timeFrom.setHours(now.getHours() - 1)

    const cookies = new Cookies();

    const urlParams = new URLSearchParams(window.location.href);
    const idToken = urlParams.get('id_token');
    if (idToken) {
        cookies.set('idToken', idToken, { path: '/' });
    }


    const [isLoading, setIsLoading] = React.useState(true);
    const [sensorList, setSensorList] = React.useState({});
    const [data, setData] = React.useState([]);
    const [dataStatus, setDataStatus] = React.useState('');
    const [isDataLoading, setIsDataLoading] = React.useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);
    const [filter, setFilter] = React.useState(
        {
            'sensor': '',
            'timeFrom': timeFrom.toISOString().slice(0, -5),
            'timeTo': now.toISOString().slice(0, -5),
            'battery': '0',
            'plot': true,
            'mean': true,
            'min': true,
            'max': true,
        }
    )
    const [alerts, setAlerts] = React.useState([])

    // log out
    function handleLogOut() {
        window.location.href = config.logout_url
        cookies.set('idToken', 'random_things', { path: '/' });
        console.log(cookies.get('idToken'))
    }

    // refresh alert list
    function handleRefreshAlerts() {
        fetch(`${config.backend_url}/get-alerts/`, {
            headers: {
                'Authorization': cookies.get('idToken')
            }
        })
            .then(res => res.json())
            .then(data => {
                const result = data.body.map(item => {
                    return [
                        item.alert_id.S,
                        item.sensor_id.S,
                        new Date(parseInt(item.timestamp.N) * 1000).toISOString().slice(0, -5).replace("T", " "),
                        item.message.S
                    ];
                });

                result.sort((a, b) => b[2].localeCompare(a[2]))
                setAlerts(result)
            })
            .catch((error) => {
                console.log('error getting alerts', error)
            });
    }

    // check authorization
    React.useEffect(() => {
        fetch(`${config.auth_url}`, {
            method: 'GET',
            headers: { 'Authorization': cookies.get('idToken') }
        })
            .then((res) => {
                if (res.ok) {
                    setIsLoading(false)
                } else {
                    window.location.href = config.login_url;
                }
            })
    })


    // refresh alert list
    React.useEffect(() => {
        handleRefreshAlerts()
        // eslint-disable-next-line
    }, [])

    // threshold for each sensor 
    React.useEffect(() => {
        if (filter.sensor) {
            fetch(`${config.backend_url}/sensor_info/${filter.sensor}`, {
                headers: {
                    'Authorization': cookies.get('idToken')
                }
            })
                .then(res => {
                    if (!res.ok) {
                        window.location.href = config.login_url;
                    }
                    return res.json()
                })
                .then(data => setFilter(prev => {
                    console.log(data)
                    return { ...prev, 'battery': data.body.battery_level }
                }))
        }
        // eslint-disable-next-line
    }, [filter.sensor])

    // get list of sensor
    React.useEffect(() => {
        console.log('fetching sensor list', config.backend_url)
        fetch(`${config.backend_url}/get-sensor-list`, {
            method: 'GET',
            headers: {
                'Authorization': cookies.get('idToken')
            }
        })
            .then((res) => {
                if (!res.ok) {
                    console.log(res.status)
                    window.location.href = config.login_url;
                }
                return res.json()
            })
            .then(data => {
                setSensorList(data.body)
            })
            .catch((error) => {
                console.log('error here')
            });
        // eslint-disable-next-line
    }, [])


    const displayData = DisplayData(data, filter)

    function makeChange(event) {
        const { name, value, type, checked } = event.target
        setFilter(prev => {
            return { ...prev, [name]: type === 'checkbox' ? checked : value }
        })
        if (['sensor', 'timeFrom', 'timeTo'].includes(name)) {
            setDataStatus('')
        }
    }

    function handleLoadData() {
        if (!filter.sensor) {
            setDataStatus('Please select a sensor first.')
            return
        }

        if (!filter.timeFrom || !filter.timeTo) {
            setDataStatus('Please enter both start and end timestamps.')
            return
        }

        const timeFromDate = new Date(filter.timeFrom)
        const timeToDate = new Date(filter.timeTo)

        if (Number.isNaN(timeFromDate.getTime()) || Number.isNaN(timeToDate.getTime())) {
            setDataStatus('Please enter a valid start and end timestamp.')
            return
        }

        if (timeFromDate > timeToDate) {
            setDataStatus('Start timestamp must be before end timestamp.')
            return
        }

        const timeFrom = timeFromDate.toISOString().slice(0, -5)
        const timeTo = timeToDate.toISOString().slice(0, -5)

        setIsDataLoading(true)
        setData([])
        setDataStatus('Loading temperature data...')
        fetch(`${config.backend_url}/temperature-data/${filter.sensor}?time_from=${timeFrom}&time_to=${timeTo}`, {
            headers: {
                'Authorization': cookies.get('idToken')
            }
        })
            .then(res => {
                if (!res.ok) {
                    window.location.href = config.login_url;
                }
                return res.json()
            })
            .then(data => {
                const temperatureData = Array.isArray(data.body) ? data.body : []
                if (temperatureData.length) {
                    setData(temperatureData)
                    setDataStatus('')
                } else {
                    setDataStatus('No data found for this sensor and time range.')
                }
            })
            .catch((error) => {
                console.log('error getting temperature data', error)
                setData([])
                setDataStatus('Unable to load temperature data. Please try again.')
            })
            .finally(() => {
                setIsDataLoading(false)
            })
    }

    const main =
        <div className='main'>
            <Topbar trigger={makeChange} value={filter} sensorList={sensorList} data={data} setData={setData} contentRef={contentRef} onLoad={handleLoadData} isDataLoading={isDataLoading} hasData={data.length > 0} />
            <div className='chart'>
                <div ref={contentRef}>
                    <Chart1 filter={filter} data={displayData} />
                </div>
                {dataStatus ? <p className='chart-status'>{dataStatus}</p> : null}
                <Checkbox trigger={makeChange} value={filter} />
            </div>
            <AlertSettings sensorId={filter.sensor} config={config} />
            <FloorPlan2 sensorId={filter.sensor} sensorList={sensorList}/>
        </div>

    function CustomNavLink({ to, label, icon: Icon }) {
        const [activeLink, setActiveLink] = React.useState(null);

        const handleNavLinkClick = () => {
            setActiveLink(to);
        };

        return (
            <li>
                <NavLink
                    to={to}
                    className={`nav-link ${activeLink === to ? 'select' : ''}`}
                    onClick={handleNavLinkClick}
                    title={isSidebarCollapsed ? label : undefined}
                >
                    <Icon className='nav-icon' size={19} strokeWidth={1.9} />
                    <span className='nav-label'>{label}</span>
                </NavLink>
            </li>
        );
    }


    if (isLoading) {
        return <div></div>
    } else {
        return (
            <div className='page'>
                <BrowserRouter>
                    <div className={`leftbar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                        <button
                            className='sidebar-toggle'
                            onClick={() => setIsSidebarCollapsed(prev => !prev)}
                            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                            title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        >
                            {isSidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                        </button>
                        <img className='logo-img' src={logo} alt='logo' />
                        <nav className='left-menu'>
                            <ul>
                                <CustomNavLink to='/' label='Chart' icon={BarChart3} />
                                <CustomNavLink to='/overview' label={`Overview`} icon={LayoutDashboard} />
                                <CustomNavLink to='/analysis' label={`AI Analysis`} icon={Sparkles} />
                                <CustomNavLink to='/alert_emails' label={`Alert Email`} icon={Mail} />
                                <CustomNavLink to='/alerts' label={`Alerts [${alerts.length}]`} icon={Bell} />
                                <CustomNavLink to='/floorplan' label={`Floor Plan`} icon={Map} />
                            </ul>
                        </nav>
                        <div className='sidebar-footer'>
                            <div className='powered-by'>
                                <img className='powered-by-logo' src={`${process.env.PUBLIC_URL}/tio-logo.png`} alt='' />
                                <div>
                                    <span>Powered by</span>
                                    <strong>Technology Innovation Office</strong>
                                </div>
                            </div>
                            <button className='logOut' onClick={handleLogOut}>
                                <LogOut size={21} strokeWidth={1.8} />
                                <span>Sign out</span>
                            </button>
                        </div>
                    </div>
                    <div className={`rightArea ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                        <Routes>
                            <Route path='/' element={main} />
                            <Route path='/overview' element={<Overview config={config} />} />
                            <Route path='/analysis' element={<Analysis sensorList={sensorList} config={config} />} />
                            <Route path='/alert_emails' element={<AlertEmail config={config} />} />
                            <Route path='/floorplan' element={<FloorPlan sensorList={sensorList}/>} />
                            <Route path='/alerts' element={<AlertList alerts={alerts} setAlerts={setAlerts} config={config} handleRefresh={handleRefreshAlerts} />} />
                        </Routes>
                    </div>
                </BrowserRouter>
            </div>
        )
    }
}

export default App;
